import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Spinner, Alert, Form, Badge } from 'react-bootstrap';
import {
    fetchTrainingRuns, fetchTrainingRun, startRegsRun,
    trainRun, carbonCopyRun, versionRun, releaseRun,
    uploadRegs, trainTenant, fetchTenants,
} from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const STATUS_VARIANT = {
    draft: 'secondary', training: 'info', carbon_copy_test: 'primary',
    versioned: 'warning', releasing: 'warning', live: 'success',
    failed: 'danger', rolled_back: 'secondary',
};

// status → the next action (label + call). 'training' is handled specially (the carbon-copy gate, with
// an optional app-version for the fix-app-&-retest path). Absent = terminal / in-progress, no button.
// `capability` names the permission the action needs (see services/permissions.js). Release is the
// four-eyes control — whoever trains a model must not be able to put it in front of members — so it
// requires waive-release while the rest require waive-training.
const NEXT_ACTION = {
    draft: { label: 'Train', run: trainRun, capability: 'trainModel' },
    carbon_copy_test: { label: 'Package release', run: (id) => versionRun(id, null), capability: 'trainModel' },
    versioned: { label: 'Release to tenants', run: releaseRun, capability: 'releaseModel' },
};

function ModelTraining() {
    const { can } = useAuth();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [notes, setNotes] = useState('');
    const [expanded, setExpanded] = useState(null);   // runId whose rollouts are shown
    const [detail, setDetail] = useState(null);       // fetched run detail (rollouts)
    const [file, setFile] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState('');
    const [retestVer, setRetestVer] = useState({});   // runId → optional app version for a carbon-copy retest
    const [verdict, setVerdict] = useState(null);     // last carbon-copy verdict (rich: missing_refs/confidence/diff)

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const data = await fetchTrainingRuns();
            setRuns(data.runs || []);
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll while any run is mid-fan-out (release is async).
    useEffect(() => {
        if (!runs.some(r => r.status === 'releasing')) return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [runs, load]);

    // Tenant list for the single-tenant train control.
    useEffect(() => {
        fetchTenants().then(d => setTenants(d.tenants || [])).catch(() => {});
    }, []);

    const doAction = async (runId, action) => {
        setBusyId(runId); setError(null);
        try {
            await action.run(runId);
            await load();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    // Carbon-copy gate. A supplied app version records a fix-app-&-retest (change_type=model_and_app).
    const doCarbonCopy = async (runId) => {
        const appVersion = (retestVer[runId] || '').trim();
        const opts = appVersion ? { changeType: 'model_and_app', appVersion } : {};
        setBusyId(runId); setError(null);
        try {
            const res = await carbonCopyRun(runId, opts);
            setVerdict({ runId, ...res });   // { passed, detail, missing_refs, confidence, diff }
            setRetestVer(v => ({ ...v, [runId]: '' }));
            await load();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    const doTrainTenant = async () => {
        if (!selectedTenant) return;
        setBusyId('tenant'); setError(null);
        try {
            await trainTenant(selectedTenant);
            setSelectedTenant('');
            await load();
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    const doStartRun = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusyId('new');
        setError(null);
        try {
            const { regs_ref } = await uploadRegs(file);          // upload → get the ref
            await startRegsRun(regs_ref, notes.trim() || null);   // then create the DRAFT run
            setFile(null);
            setNotes('');
            e.target.reset();
            await load();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusyId(null);
        }
    };

    const toggleDetail = async (runId) => {
        if (expanded === runId) { setExpanded(null); setDetail(null); return; }
        setExpanded(runId); setDetail(null);
        try { setDetail(await fetchTrainingRun(runId)); }
        catch (e) { setError(errMsg(e)); }
    };

    function errMsg(e) {
        const d = e?.response?.data?.detail;
        if (typeof d === 'string') return d;
        if (Array.isArray(d)) return d.map(x => `${(x.loc || []).slice(1).join('.')}: ${x.msg}`).join('; ');
        return e?.message || 'Request failed';
    }

    // Whether the golden-diff has any ref/step changes worth surfacing (a confidence-only shift is not).
    const diffHasChanges = (d) => Boolean(d && (
        (d.refs_added || []).length || (d.refs_removed || []).length ||
        Object.keys(d.steps_changed || {}).length
    ));

    // The action control for a run row. 'training' → the carbon-copy gate + an optional retest app-version.
    const actionCell = (r) => {
        if (r.status === 'training') {
            return (
                <div className="d-flex gap-1 align-items-center">
                    <Form.Control size="sm" style={{ maxWidth: 140 }} placeholder="app ver (retest)"
                        value={retestVer[r.run_id] || ''}
                        onChange={e => setRetestVer(v => ({ ...v, [r.run_id]: e.target.value }))} />
                    <Button size="sm" disabled={busyId === r.run_id || !can.trainModel()}
                        title={can.trainModel() ? undefined : 'Requires the waive-training role'}
                        onClick={() => doCarbonCopy(r.run_id)}>
                        {busyId === r.run_id ? <Spinner size="sm" /> : 'Run carbon-copy'}
                    </Button>
                </div>
            );
        }
        const action = NEXT_ACTION[r.status];
        if (!action) return null;
        // Disabled rather than hidden: the reviewer should SEE that a release is the next step and that
        // it needs someone else — hiding it makes the workflow look stuck.
        const allowed = can[action.capability]();
        return (
            <Button size="sm" disabled={busyId === r.run_id || !allowed}
                title={allowed ? undefined
                    : `Requires the ${action.capability === 'releaseModel' ? 'waive-release' : 'waive-training'} role`}
                onClick={() => doAction(r.run_id, action)}>
                {busyId === r.run_id ? <Spinner size="sm" /> : action.label}
            </Button>
        );
    };

    return (
        <div className="page-content">
            <div className="page-header">
                <h1 className="page-title">Model Training</h1>
            </div>
            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

            {verdict && (
                <Alert variant={verdict.passed ? 'success' : 'warning'} onClose={() => setVerdict(null)} dismissible>
                    <div><strong>Carbon-copy gate {verdict.passed ? 'passed' : 'failed'}</strong> — run <code>{verdict.runId.slice(0, 8)}</code></div>
                    {verdict.detail && <div>{verdict.detail}</div>}
                    {typeof verdict.confidence === 'number' &&
                        <div>Aggregate confidence: {verdict.confidence.toFixed(3)}</div>}
                    {(verdict.missing_refs || []).length > 0 &&
                        <div>Missing anchor refs: <code>{verdict.missing_refs.join(', ')}</code></div>}
                    {diffHasChanges(verdict.diff) && (
                        <div className="mt-2">
                            <strong>Golden-diff vs approved baseline</strong> <span className="text-muted">(review only — does not gate)</span>
                            <ul className="mb-0">
                                {(verdict.diff.refs_added || []).length > 0 &&
                                    <li>Added refs: {verdict.diff.refs_added.join(', ')}</li>}
                                {(verdict.diff.refs_removed || []).length > 0 &&
                                    <li>Removed refs: {verdict.diff.refs_removed.join(', ')}</li>}
                                {Object.keys(verdict.diff.steps_changed || {}).length > 0 &&
                                    <li>Changed steps: {Object.keys(verdict.diff.steps_changed).join(', ')}</li>}
                                {verdict.diff.confidence_delta
                                    ? <li>Confidence delta: {verdict.diff.confidence_delta}</li> : null}
                            </ul>
                        </div>
                    )}
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Start a regs training run</Card.Title>
                    <Form onSubmit={doStartRun} className="d-flex gap-2 align-items-end flex-wrap">
                        <Form.Group>
                            <Form.Label>Regs document (PDF / text)</Form.Label>
                            <Form.Control type="file" accept=".pdf,.txt,.md"
                                onChange={e => setFile(e.target.files[0] || null)} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Notes</Form.Label>
                            <Form.Control value={notes} onChange={e => setNotes(e.target.value)}
                                placeholder="optional" style={{ minWidth: 240 }} />
                        </Form.Group>
                        <Button type="submit" disabled={busyId === 'new' || !file}>
                            {busyId === 'new' ? <Spinner size="sm" /> : 'Start run'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Train a single tenant</Card.Title>
                    <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                        Onboarding or a fund change — retrain one tenant on the last released regs data + its funds.
                    </div>
                    <div className="d-flex gap-2 align-items-end flex-wrap">
                        <Form.Group>
                            <Form.Label>Tenant</Form.Label>
                            <Form.Select value={selectedTenant} style={{ minWidth: 240 }}
                                onChange={e => setSelectedTenant(e.target.value)}>
                                <option value="">Select a tenant…</option>
                                {tenants.filter(t => t.active !== false).map(t => (
                                    <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Button variant="secondary" disabled={!selectedTenant || busyId === 'tenant'}
                            onClick={doTrainTenant}>
                            {busyId === 'tenant' ? <Spinner size="sm" /> : 'Train tenant'}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Card.Title className="mb-0">Runs</Card.Title>
                        <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Refresh'}
                        </Button>
                    </div>
                    <Table hover size="sm" responsive>
                        <thead>
                            <tr><th></th><th>Run</th><th>Scope</th><th>Status</th><th>Model version</th><th>Created</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                            {runs.map(r => (
                                <React.Fragment key={r.run_id}>
                                    <tr>
                                        <td><Button variant="link" size="sm" onClick={() => toggleDetail(r.run_id)}>
                                            {expanded === r.run_id ? '▾' : '▸'}</Button></td>
                                        <td><code>{r.run_id.slice(0, 8)}</code></td>
                                        <td>{r.scope}{r.target_tenant ? ` (${r.target_tenant})` : ''}</td>
                                        <td><Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>{r.status}</Badge></td>
                                        <td>{r.regs_data_version || '—'}</td>
                                        <td>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                                        <td>{actionCell(r)}</td>
                                    </tr>
                                    {expanded === r.run_id && (
                                        <tr><td colSpan={7}>
                                            {!detail ? <Spinner size="sm" /> : (
                                                <Table size="sm" bordered className="mb-0">
                                                    <thead><tr><th>Tenant</th><th>Status</th><th>Model version</th><th>Detail</th></tr></thead>
                                                    <tbody>
                                                        {(detail.rollouts || []).length === 0
                                                            ? <tr><td colSpan={4}><em>No rollouts yet</em></td></tr>
                                                            : detail.rollouts.map(ro => (
                                                                <tr key={ro.tenant_code}>
                                                                    <td>{ro.tenant_code}</td>
                                                                    <td><Badge bg={STATUS_VARIANT[ro.status] || 'secondary'}>{ro.status}</Badge></td>
                                                                    <td>{ro.model_version || '—'}</td>
                                                                    <td>{ro.detail || '—'}</td>
                                                                </tr>))}
                                                    </tbody>
                                                </Table>)}
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
}

export default ModelTraining;
